import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, eur } from "../lib/api";
import { MONTHS_IT } from "../lib/season";
import MonthSelector from "../components/MonthSelector";
import { useConfig } from "../context/ConfigContext";
import { useAuth } from "../context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { ScanLine, Users, Utensils, Wallet, TrendingUp, ArrowRight, AlertTriangle, UserPlus, Bot, Recycle } from "lucide-react";
import { expiryStatus, STATUS_STYLE } from "../lib/haccp";

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="tnum font-head text-2xl font-bold text-navy">{value}</div>
      <div className="mt-1 text-sm text-slate-500">{label}</div>
    </div>
  );
}

function BudgetBar({ budget, spent, onSetup }) {
  const over = budget > 0 && spent > budget;
  const pct = budget > 0 ? Math.round((spent / budget) * 100) : 0;
  const barColor = pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-emerald-500";
  if (budget <= 0) {
    return (
      <div data-testid="budget-card" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-4">
        <span className="text-sm text-slate-500">Nessun budget mensile impostato dall'Armatore.</span>
        <button data-testid="budget-setup-link" onClick={onSetup} className="text-sm font-semibold text-navy underline">Impostalo in Impostazioni</button>
      </div>
    );
  }
  return (
    <div data-testid="budget-card" className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-sm text-slate-500">Budget mensile Armatore</div>
          <div className="tnum font-head text-2xl font-bold text-navy">{eur(budget)}</div>
        </div>
        <div className="text-right">
          <div className="text-sm text-slate-500">Speso questo mese</div>
          <div data-testid="budget-spent" className={`tnum font-head text-2xl font-bold ${over ? "text-red-600" : "text-navy"}`}>{eur(spent)}</div>
        </div>
      </div>
      <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="mt-2 flex items-center justify-between text-sm">
        <span data-testid="budget-remaining" className={over ? "font-semibold text-red-600" : "text-slate-500"}>
          {over ? `Sforato di ${eur(spent - budget)}` : `Rimanente ${eur(budget - spent)}`}
        </span>
        <span className={`font-semibold ${over ? "text-red-600" : "text-navy"}`}>{pct}%</span>
      </div>
      {pct >= 80 && (
        <div data-testid="budget-warning" className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold ${over ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>
          {over ? "Budget del mese superato" : "Attenzione: superato l'80% del budget del mese"}
        </div>
      )}
    </div>
  );
}

function MiniBudget({ label, budget, spent, covers }) {
  const pct = budget > 0 ? Math.round((spent / budget) * 100) : 0;
  const over = budget > 0 && spent > budget;
  const barColor = pct >= 100 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div data-testid={`mini-budget-${label.includes("Ciurma") ? "crew" : "guests"}`} className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-end justify-between gap-2">
        <div className="text-sm font-semibold text-navy">{label}</div>
        <div className="text-xs text-slate-500">Budget {eur(budget)}{covers != null ? ` · ${covers} coperti` : ""}</div>
      </div>
      <div className="mt-1 tnum font-head text-xl font-bold text-navy">{eur(spent)}</div>
      <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-xs">
        <span className={over ? "font-semibold text-red-600" : "text-slate-500"}>{over ? `Sforato di ${eur(spent - budget)}` : `Rimanente ${eur(budget - spent)}`}</span>
        <span className={`font-semibold ${over ? "text-red-600" : "text-navy"}`}>{pct}%</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [month, setMonth] = useState(6);
  const [data, setData] = useState(null);
  const [expItems, setExpItems] = useState([]);
  const [season, setSeason] = useState(null);
  const [marpol, setMarpol] = useState(null);
  const { config } = useConfig();
  const { user } = useAuth();
  const isManager = user?.role === "chef" || user?.role === "comandante";
  const navigate = useNavigate();

  const load = () => {
    api.get(`/dashboard/2026/${month}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(load, [month]);
  useEffect(() => {
    api.get("/budget/season").then((r) => setSeason(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    Promise.all([api.get("/marpol/inventory"), api.get("/marpol/awn")]).then(([inv, awn]) => {
      const onboard = (inv.data || []).reduce((s, i) => s + (i.current_quantity_m3 || 0), 0);
      const next = (awn.data || []).filter((a) => a.status !== "confirmed").sort((a, b) => new Date(a.eta) - new Date(b.eta))[0];
      setMarpol({ onboard, next });
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const warn = config?.haccp_warn_days ?? 7;
    api.get("/haccp").then((r) => {
      setExpItems(
        r.data
          .map((i) => ({ ...i, s: expiryStatus(i.expiry, warn) }))
          .filter((i) => i.s.key === "expired" || i.s.key === "soon")
          .sort((a, b) => (a.s.days ?? 999) - (b.s.days ?? 999))
      );
    }).catch(() => {});
  }, [config?.haccp_warn_days]);
  useEffect(() => {
    window.addEventListener("data-synced", load);
    return () => window.removeEventListener("data-synced", load);
  });

  const catData = (data?.by_category || []).filter((c) => c.amount > 0).map((c) => ({ name: c.name, val: c.amount }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-navy">Dashboard</h1>
          <p className="text-slate-500">{config?.name} · Stagione Estate 2026</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isManager && (
            <button
              data-testid="dashboard-assistant-cta"
              onClick={() => window.open("/assistente", "_blank")}
              className="flex h-14 items-center gap-2 rounded-xl border-2 border-gold px-6 text-lg font-bold text-navy transition-transform hover:-translate-y-0.5"
            >
              <Bot className="h-6 w-6" /> Assistente Stand
            </button>
          )}
          {isManager && (
            <button
              data-testid="dashboard-new-user-cta"
              onClick={() => navigate("/utenti", { state: { focusCreate: true } })}
              className="flex h-14 items-center gap-2 rounded-xl border-2 border-navy px-6 text-lg font-bold text-navy transition-transform hover:-translate-y-0.5"
            >
              <UserPlus className="h-6 w-6" /> Nuovo accesso
            </button>
          )}
          <button
            data-testid="dashboard-scan-cta"
            onClick={() => navigate("/scan")}
            className="flex h-14 items-center gap-2 rounded-xl bg-gold px-6 text-lg font-bold text-black transition-transform hover:-translate-y-0.5"
          >
            <ScanLine className="h-6 w-6" /> Scansiona fattura
          </button>
        </div>
      </div>

      <MonthSelector month={month} onChange={setMonth} />

      {data && <BudgetBar budget={Number((config?.monthly_budgets && config.monthly_budgets[`2026-${String(month).padStart(2, "0")}`]) ?? config?.monthly_budget) || 0} spent={data.total_spend} onSetup={() => navigate("/setup")} />}
      {data && (data.guest_budget > 0 || data.crew_budget > 0) && (
        <div data-testid="split-budget" className="grid gap-4 sm:grid-cols-2">
          <MiniBudget label="Ospiti / Clienti" budget={data.guest_budget || 0} spent={data.spent_guests || 0} covers={data.covers_guests} />
          <MiniBudget label="Ciurma" budget={data.crew_budget || 0} spent={data.spent_crew || 0} covers={data.covers_crew} />
        </div>
      )}
      {season && season.budget > 0 && (
        <div data-testid="season-budget-card" className="rounded-xl border border-navy/20 bg-navy p-5 text-white">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-sm text-slate-300">Budget stagione (Giu–Ott 2026)</div>
              <div className="tnum font-head text-2xl font-bold text-gold">{eur(season.budget)}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-slate-300">Speso in stagione</div>
              <div className="tnum font-head text-2xl font-bold text-white">{eur(season.spent)}</div>
            </div>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-white/15">
            <div className={`h-full rounded-full ${season.pct >= 100 ? "bg-red-500" : season.pct >= 75 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, season.pct)}%` }} />
          </div>
          <div className="mt-2 flex items-center justify-between text-sm">
            <span className="text-slate-300">{season.remaining >= 0 ? `Rimanente ${eur(season.remaining)}` : `Sforato di ${eur(-season.remaining)}`}</span>
            <span className="font-semibold text-gold">{season.pct}%</span>
          </div>
        </div>
      )}

      {!data ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-200" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Wallet} label="Food cost / persona / giorno" value={eur(data.food_cost_per_cover)} accent="bg-gold/20 text-gold" />
            <Kpi icon={Utensils} label="Spesa alimentare" value={eur(data.food_spend)} accent="bg-emerald-100 text-emerald-600" />
            <Kpi icon={Users} label="Coperti totali" value={data.total_covers} accent="bg-blue-100 text-blue-600" />
            <Kpi icon={TrendingUp} label="Spesa totale (mese)" value={eur(data.total_spend)} accent="bg-navy/10 text-navy" />
          </div>

          {marpol && (
            <button data-testid="dashboard-marpol-tile" onClick={() => navigate("/marpol")} className="flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-gold">
              <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${marpol.onboard > 1 ? "bg-red-100 text-red-600" : marpol.onboard > 0.5 ? "bg-amber-100 text-amber-600" : "bg-emerald-100 text-emerald-600"}`}><Recycle className="h-6 w-6" /></div>
                <div>
                  <div className="font-head text-2xl font-bold text-navy">{marpol.onboard.toFixed(2)} m³ <span className="text-sm font-normal text-slate-400">rifiuti a bordo</span></div>
                  <div className="text-sm text-slate-500">{marpol.next ? `Prossima consegna: ${marpol.next.port_of_arrival?.name || "porto"} · ${new Date(marpol.next.eta).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}` : "MARPOL · nessuna consegna programmata"}</div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-gold" />
            </button>
          )}

          {expItems.length > 0 && (
            <div data-testid="dashboard-expiry-card" className="rounded-xl border border-amber-300 bg-amber-50 p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-head text-lg font-semibold text-navy"><AlertTriangle className="h-5 w-5 text-amber-600" /> Prodotti in scadenza</h3>
                <button data-testid="dashboard-expiry-link" onClick={() => navigate("/scadenze")} className="text-sm font-semibold text-navy underline">Vedi registro</button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {expItems.slice(0, 6).map((i) => (
                  <div key={i.id} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2 text-sm">
                    <span className="truncate font-medium text-navy">{i.product || "Senza nome"}</span>
                    <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[i.s.key]}`}>{i.s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {data.inventory_used && (
            <div data-testid="inventory-badge" className={`rounded-lg border px-4 py-2.5 text-sm ${data.consumption < 0 ? "border-amber-400 bg-amber-50 text-amber-800" : "border-gold/40 bg-gold/10 text-navy"}`}>
              {data.consumption < 0 ? (
                <>Attenzione: la <b>giacenza a fine mese</b> supera giacenza iniziale + acquisti, quindi il consumo risulta negativo ({eur(data.consumption)}). Verifica il valore delle giacenze inserito.</>
              ) : (
                <>Food cost calcolato con <b>giacenze mensili</b> (consumo reale: {eur(data.consumption)}).</>
              )}
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 font-head text-lg font-semibold text-navy">Spesa per categoria — {MONTHS_IT[month]}</h3>
              {catData.length === 0 ? (
                <p className="py-10 text-center text-slate-400">Nessuna spesa registrata questo mese</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={catData} layout="vertical" margin={{ left: 20 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v) => eur(v)} />
                    <Bar dataKey="val" fill="#0B132B" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-4 font-head text-lg font-semibold text-navy">Andamento food cost — stagione</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={(data.trend || []).map((t) => ({ name: MONTHS_IT[t.month].slice(0, 3), fc: t.food_cost }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Line type="monotone" dataKey="fc" stroke="#D4AF37" strokeWidth={3} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {(data.spent_guests > 0 || data.spent_crew > 0) && (
            <div data-testid="split-pie-card" className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="mb-2 font-head text-lg font-semibold text-navy">Ripartizione spesa — Ospiti vs Ciurma ({MONTHS_IT[month]})</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={[{ name: "Ospiti / Clienti", value: data.spent_guests || 0 }, { name: "Ciurma", value: data.spent_crew || 0 }]}
                    dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95}
                    label={(e) => `${e.name}: ${eur(e.value)}`}
                  >
                    <Cell fill="#D4AF37" />
                    <Cell fill="#0B132B" />
                  </Pie>
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { to: "/acquisti", label: "Nuovo acquisto", icon: Wallet },
              { to: "/coperti", label: "Coperti del giorno", icon: Users },
              { to: "/report", label: "Report Armatore", icon: ArrowRight },
            ].map((a) => (
              <button key={a.to} onClick={() => navigate(a.to)} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-5 text-left transition-colors hover:border-gold">
                <span className="font-semibold text-navy">{a.label}</span>
                <ArrowRight className="h-5 w-5 text-gold" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
